if (!customElements.get('catalog-products')) {
  customElements.define(
    'catalog-products',
    class CatalogProducts extends HTMLElement {
      constructor() {
        super();

        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => this.activateTab(tab));
          tab.addEventListener('keydown', (event) => this.handleTabKeydown(event, tab));
        });

        this.addEventListener('change', (event) => {
          if (event.target.matches('[data-catalog-variant]')) this.updateVariant(event.target);
        });

        this.addEventListener('click', (event) => {
          const addButton = event.target.closest('[data-catalog-add]');
          if (addButton) {
            this.addToCart(addButton);
            return;
          }

          const loadMoreButton = event.target.closest('[data-catalog-load-more]');
          if (loadMoreButton) this.loadMore(loadMoreButton);
        });

        const hashTab = this.tabs.find((tab) => tab.dataset.catalogHandle === window.location.hash.slice(1));
        this.activateTab(hashTab || this.tabs[0], false);
        this.classList.add('catalog-products--ready');
      }

      activateTab(activeTab, updateHash = true) {
        if (!activeTab) return;

        this.tabs.forEach((tab) => {
          const isActive = tab === activeTab;
          tab.setAttribute('aria-selected', String(isActive));
          tab.tabIndex = isActive ? 0 : -1;
        });

        this.panels.forEach((panel) => {
          panel.hidden = panel.id !== activeTab.getAttribute('aria-controls');
        });

        if (updateHash && window.history && window.history.replaceState) {
          window.history.replaceState(null, '', `#${activeTab.dataset.catalogHandle}`);
        }
      }

      handleTabKeydown(event, currentTab) {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

        event.preventDefault();
        const currentIndex = this.tabs.indexOf(currentTab);
        let nextIndex = currentIndex;

        if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = this.tabs.length - 1;

        this.activateTab(this.tabs[nextIndex]);
        this.tabs[nextIndex].focus();
      }

      updateVariant(select) {
        const option = select.options[select.selectedIndex];
        const card = select.closest('[data-catalog-card]');
        if (!option || !card) return;

        const price = card.querySelector('[data-catalog-price]');
        const button = card.querySelector('[data-catalog-add]');
        const label = card.querySelector('[data-catalog-add-label]');
        const isAvailable = option.dataset.available === 'true';

        if (price) {
          const original = price.querySelector('[data-revlift-original-price]');
          if (original) {
            original.innerHTML = `<span>${option.dataset.price || ''}</span>${
              option.dataset.comparePrice ? `<s>${option.dataset.comparePrice}</s>` : ''
            }`;
          }
          price.dataset.revliftBaseCents = option.dataset.priceCents || '0';
          price.dataset.revliftVariantId = option.value || '';
          window.RevLiftPricing?.syncStoreWidePricing?.();
        }

        if (button) button.disabled = !isAvailable;
        if (label) label.textContent = isAvailable ? window.variantStrings.addToCart : window.variantStrings.soldOut;
      }

      async addToCart(button) {
        if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;

        const productForm = button.closest('[data-catalog-form]');
        const variantInput = productForm?.querySelector('[name="id"]');
        const quantityInput = productForm?.querySelector('[name="quantity"]');
        const spinner = button.querySelector('.loading__spinner');
        const errorWrapper = productForm?.querySelector('.product-form__error-message-wrapper');
        const errorMessage = errorWrapper?.querySelector('.product-form__error-message');
        if (!productForm || !variantInput || !quantityInput) return;

        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
        spinner?.classList.remove('hidden');
        errorWrapper?.setAttribute('hidden', 'hidden');

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const formData = new FormData();
        formData.append('id', variantInput.value);
        formData.append('quantity', quantityInput.value || '1');

        if (cart) {
          formData.append(
            'sections',
            cart
              .getSectionsToRender()
              .map((section) => section.id)
              .join(',')
          );
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement?.(button);
        }

        try {
          const response = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: formData,
          });
          const result = await response.json();

          if (!response.ok || result.status) {
            throw new Error(result.description || result.message || 'This item could not be added to the cart.');
          }

          if (cart) {
            cart.renderContents(result);
          } else {
            window.location.assign(window.routes.cart_url);
          }
        } catch (error) {
          if (errorMessage) errorMessage.textContent = error.message;
          errorWrapper?.removeAttribute('hidden');
        } finally {
          button.removeAttribute('aria-disabled');
          button.classList.remove('loading');
          spinner?.classList.add('hidden');
        }
      }

      async loadMore(button) {
        if (button.disabled) return;

        const panel = button.closest('[role="tabpanel"]');
        const grid = panel?.querySelector('[data-catalog-grid]');
        const page = button.dataset.page;
        const originalLabel = button.textContent;
        if (!panel || !grid || !page) return;

        button.disabled = true;
        button.textContent = button.dataset.loadingLabel;

        try {
          const url = new URL(window.location.href);
          url.hash = '';
          url.searchParams.set('section_id', this.dataset.sectionId);
          url.searchParams.set('page', page);

          const response = await fetch(url.toString());
          if (!response.ok) throw new Error(`Catalog request failed: ${response.status}`);

          const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
          const nextPanel = documentFragment.getElementById(panel.id);
          const nextGrid = nextPanel?.querySelector('[data-catalog-grid]');
          if (!nextGrid) throw new Error('Catalog response did not include the requested category.');

          Array.from(nextGrid.children).forEach((productCard) => grid.appendChild(productCard));

          const nextButton = nextPanel.querySelector('[data-catalog-load-more]');
          if (nextButton) {
            button.dataset.page = nextButton.dataset.page;
            button.disabled = false;
            button.textContent = originalLabel;
          } else {
            button.remove();
          }
        } catch (error) {
          console.error(error);
          button.disabled = false;
          button.textContent = button.dataset.errorLabel;
          window.setTimeout(() => {
            button.textContent = originalLabel;
          }, 2500);
        }
      }
    }
  );
}
